import { action, computed, makeObservable, observable } from 'mobx';
import { AxiosResponse } from 'axios';
import merge from 'lodash/merge';

import { ApiPermission } from '@api';
import { Region } from '@components/common/constants';
import { getApiErrorMessage } from '@components/common/util/util';
import { hasExhaustedLimit } from '@components/directory/utils';
import { apiAuthCaller } from '@services/apiCaller';
import { PlanNameType } from '@stores/billing';
import { IRootStore } from '@stores/index';
import {
  LegacyInviteRole,
  Member,
  OrganizationRole,
  ServerStatus,
  UserRoles,
} from '@stores/member';
import { BotBlockingEventManagementConfig } from '@stores/types';
import HttpStatusCode from '@utils/httpStatusCodes';

type BotBlockingDetectionConfig = {
  enabled: boolean;
};

type BotBlockingWorkSpaceConfig = {
  detection?: BotBlockingDetectionConfig;
  eventManagement?: BotBlockingEventManagementConfig;
};

export type WorkspaceEnvironment = 'DEVELOPMENT' | 'PRODUCTION';

type ServerOrgFeature = Record<
  'enableDeviceModeTransformations' | `enable_${string}_source`,
  boolean
>;

type Metadata = Record<string, unknown>;

type ServerWorkspace = {
  id: string;
  name: string;
  createdAt: string;
  dataPlaneURL: string;
  ownerId: string;
  userRoles: {
    user: { id: string; email: string; name: string };
    roles: UserRoles;
  }[];
  invitedUsers: {
    id: string;
    name: string;
    email: string;
    status: ServerStatus;
    role: LegacyInviteRole;
    userRoles: UserRoles;
    createdAt: string;
  }[];
  organizationId: string;
  orgSettings: {
    name: string;
    enforcedMfa: boolean;
    grafanaURL?: string;
  } & ServerOrgFeature;
  piiPermissions: { isLocked: boolean; id: string };
  defaultRegion: Region;
  dataplanes: { region: Region; url: string }[];
  sshConfig: { publicKey: string; id: string } | undefined;
  planName: PlanNameType;
  environment: WorkspaceEnvironment;
  eventAuditEnabled: boolean;
  metadata: Metadata;
  botBlocking?: BotBlockingWorkSpaceConfig;
  accessPolicy: ApiPermission[];
};

type Onboarding = 'addSource' | 'liveEvents';

export interface IWorkspace {
  id: string;
  name: string;
  createdAt: string;
  dataPlane: string;
  isLoading: boolean;
  hasExhaustedCloudLimit: boolean;
  cloudSourcesLimit?: number;
  hasExhaustedWHSourcesLimit: boolean;
  warehouseActionsLimit?: number;
  hasExhaustedTransformationsLimit: boolean;
  transformationsLimit?: number;
  firstLoad: boolean;
  enableDeviceModeTransformations: boolean;
  enabledSources: string[];
  features: Map<string, string[]>;
  tasks: undefined | string[];
  showOnboarding: Record<Onboarding, boolean>;
  botBlocking: BotBlockingWorkSpaceConfig;
  accessPolicy: ApiPermission[];
  setShowOnboarding: (key: Onboarding, val: boolean) => void;
  getTasks: () => void;
  markTaskComplete: (task: string) => void;
  updateBotDetectionConfig: (config: BotBlockingDetectionConfig) => Promise<void>;
  updateBotEventManagementConfig: (
    config: Partial<BotBlockingEventManagementConfig>,
  ) => Promise<void>;
  dataResidencyDefaultRegion: Region;
  dataplanes: { region: Region; url: string }[];
  sshConfig: { publicKey: string; id: string };
  dataplaneRegions: Region[];
  reportingRegions: Region[];
  defaultRegion: Region;
  environment: WorkspaceEnvironment;
  eventAuditEnabled: boolean;
  metadata: Metadata;
}

export type Bucket = {
  type: 'S3' | 'GCS' | 'AZURE_BLOB' | 'MINIO';
  config: Record<string, unknown>;
};

export interface RetentionData {
  enableReportingPii: boolean;
  useRudderServerStorage: boolean;
  bucket: Bucket | null;
  retentionPeriod?: 'full' | 'default';
}

type ReportingPiiStatus = 'enabled' | 'disabled' | 'noAccess' | 'idle';

// If a new module/feature comes up, add it over here:
type IFeatureModule = 'sources' | 'warehouse';
type IFeature = 'configurationTests';

export interface IWorkspaceStore extends IWorkspace {
  rootStore: IRootStore;
  getWorkspace(): Promise<void>;
  getSettings: () => Promise<void>;
  requestAdminToUpgrade: () => Promise<boolean>;
  getGrafanaUrl: () => Promise<string | false>;
  updateName: (e: string) => Promise<boolean>;
  reset: () => void;
  isFeatureEnabled: (module: IFeatureModule, feature: IFeature) => boolean;
  getFeatures: () => Promise<void>;
  members: Member[];
  reportingPiiStatus: ReportingPiiStatus;
  updateReportingPiiStatus: (enableReportingPii: boolean) => void;
  getReportingPiiStatus: () => Promise<RetentionData | null>;
  updateEventAudit: (enabled: boolean) => void;
}

export class WorkspaceStore implements IWorkspaceStore {
  @observable public id = '';

  @observable public createdAt = '';

  @observable public name = '';

  @observable public isLoading = false;

  @observable public cloudSourcesLimit = 0;

  @observable public warehouseActionsLimit = 0;

  @observable public rootStore: IRootStore;

  @observable public dataPlaneURL = '';

  @observable public firstLoad = false;

  @observable public transformationsLimit = 0;

  @observable public enableDeviceModeTransformations = false;

  @observable public enabledSources: string[] = [];

  @observable public features: Map<string, string[]> = new Map();

  @observable public showOnboarding = {} as Record<Onboarding, boolean>;

  @observable public tasks: IWorkspace['tasks'];

  @observable public defaultRegion: Region = Region.US;

  @observable public dataResidencyDefaultRegion: Region = Region.US;

  @observable public dataplanes: IWorkspace['dataplanes'] = [];

  @observable public sshConfig: IWorkspace['sshConfig'] = { publicKey: '', id: '' };

  @observable public environment: WorkspaceEnvironment = 'PRODUCTION';

  @observable public reportingPiiStatus: ReportingPiiStatus = 'idle';

  @observable public eventAuditEnabled = false;

  @observable public botBlocking: IWorkspace['botBlocking'] = {};

  @observable public accessPolicy: IWorkspace['accessPolicy'] = [];

  public metadata: Metadata = {};

  /** Add variables in {@link reset} method as well */

  constructor(rootStore: IRootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action.bound
  public async getWorkspace() {
    const { userStore } = this.rootStore;
    const { selectedWorkspaceId } = userStore;

    if (!selectedWorkspaceId) {
      userStore.signOutUser();
      return;
    }

    await this.getSettings();
    this.firstLoad = true;
  }

  @action.bound
  public isFeatureEnabled(module: IFeatureModule, feature: IFeature) {
    const moduleFeatures = this.features.get(module);
    return !!(moduleFeatures && moduleFeatures.includes(feature));
  }

  @action.bound
  public async getFeatures() {
    apiAuthCaller()
      .get('/features')
      .then((res: AxiosResponse<{ features: Map<string, string[]> }>) => {
        this.features = new Map(Object.entries(res.data.features));
      });
  }

  getEnabledSources(settings: ServerOrgFeature) {
    const { sourceDefinitionsListStore } = this.rootStore;
    return sourceDefinitionsListStore.sourceDefinitions
      .filter(({ name }) => settings[`enable_${name.toLowerCase()}_source`])
      .map(({ name }) => name);
  }

  public async getSettings() {
    const { userStore, permissionsStore, organizationStore } = this.rootStore;
    try {
      const resp = await apiAuthCaller().get<{
        workspace: ServerWorkspace;
        metadata: { userId: string; profile: Metadata };
        isEligibleForTrial: boolean;
      }>('/settings');
      const workspaceData = resp.data.workspace;
      const { metadata, isEligibleForTrial } = resp.data;
      this.id = workspaceData.id;
      this.createdAt = workspaceData.createdAt;
      this.name = workspaceData.name;
      this.isLoading = false;
      this.dataPlaneURL = workspaceData.dataPlaneURL;
      if (workspaceData.sshConfig) {
        this.sshConfig = workspaceData.sshConfig;
      }
      this.enableDeviceModeTransformations =
        workspaceData.orgSettings.enableDeviceModeTransformations;
      this.enabledSources = this.getEnabledSources(workspaceData.orgSettings);
      this.dataResidencyDefaultRegion =
        workspaceData.dataplanes.length > 1 ? workspaceData.defaultRegion : Region.US;
      this.defaultRegion = workspaceData.defaultRegion;
      this.setDataplanes(workspaceData);
      this.environment = workspaceData.environment;
      this.eventAuditEnabled = workspaceData.eventAuditEnabled || false;
      this.metadata = metadata.profile;
      this.botBlocking = workspaceData.botBlocking || {};
      this.accessPolicy = workspaceData.accessPolicy || [];
      permissionsStore.setDataPermissions(workspaceData.piiPermissions);
      organizationStore.seed({
        id: workspaceData.organizationId,
        name: workspaceData.orgSettings.name,
        planName: workspaceData.planName,
        grafanaURL: workspaceData.orgSettings.grafanaURL,
        ownerId: workspaceData.ownerId,
        userRoles: workspaceData.userRoles,
        invitedUsers: workspaceData.invitedUsers,
        workspaceId: workspaceData.id,
        enforcedMfa: workspaceData.orgSettings.enforcedMfa,
        isEligibleForTrial: isEligibleForTrial ?? false,
      });
      userStore.identifyFoundation();
    } catch (err) {}
  }

  private setDataplanes = (workspaceData: ServerWorkspace) => {
    const { dataPlaneURL, dataplanes } = workspaceData;
    if (dataplanes.length > 0) {
      this.dataplanes = dataplanes;
    } else if (dataPlaneURL) {
      this.dataplanes = [{ region: this.dataResidencyDefaultRegion, url: dataPlaneURL }];
    } else {
      this.dataplanes = [];
    }
  };

  @computed get hasExhaustedCloudLimit() {
    return hasExhaustedLimit({
      limit: this.cloudSourcesLimit,
      count: this.rootStore.sourcesListStore.cloudSourcesCount,
    });
  }

  @computed get hasExhaustedWHSourcesLimit() {
    return hasExhaustedLimit({
      limit: this.warehouseActionsLimit,
      count: this.rootStore.sourcesListStore.warehouseSourcesCount,
    });
  }

  @action.bound
  public async requestAdminToUpgrade() {
    try {
      const resp = await apiAuthCaller({ region: Region.US }).post('/requestAdminToUpgrade');
      return resp.status === 200;
    } catch (error) {
      // Handle error
      return false;
    }
  }

  @action.bound
  public async getGrafanaUrl() {
    try {
      const response = await apiAuthCaller({ nonWorkspaceRoute: true }).get<string>(
        `/grafana/${this.id}/authenticate`,
      );
      return response.data;
    } catch (error) {
      return false;
    }
  }

  @action.bound
  public async updateName(name: string) {
    const {
      messagesStore: { showErrorMessage },
    } = this.rootStore;
    try {
      const resp = await apiAuthCaller().post('/updateName', { name });
      if (resp.status === HttpStatusCode.Ok) {
        this.name = name;
        return true;
      }
      return false;
    } catch (error) {
      showErrorMessage(getApiErrorMessage(error, 'Failed to update workspace name'));
      return false;
    }
  }

  @action.bound
  setShowOnboarding(key: Onboarding, val: boolean) {
    this.showOnboarding[key] = val;
  }

  @action.bound
  public updateReportingPiiStatus(enableReportingPii: boolean) {
    this.reportingPiiStatus = enableReportingPii ? 'enabled' : 'disabled';
  }

  @action.bound
  public async updateEventAudit(enabled: boolean) {
    this.eventAuditEnabled = enabled;
  }

  @action.bound
  public async updateBotEventManagementConfig(config: Partial<BotBlockingEventManagementConfig>) {
    const {
      messagesStore: { showErrorMessage },
    } = this.rootStore;
    const existingConfig = this.botBlocking.eventManagement;
    try {
      const newConfig = merge({}, existingConfig, config);
      if (newConfig.enabled && !newConfig.action) {
        newConfig.action = existingConfig?.action || 'flag';
      }
      this.botBlocking.eventManagement = newConfig;
      await apiAuthCaller().patch('/bot-event-management', newConfig);
    } catch (error) {
      this.botBlocking.eventManagement = existingConfig;
      showErrorMessage(getApiErrorMessage(error, 'Failed to update bot event management config'));
    }
  }

  @action.bound
  public async updateBotDetectionConfig(config: BotBlockingDetectionConfig) {
    const {
      messagesStore: { showErrorMessage },
    } = this.rootStore;
    const existingConfig = this.botBlocking.detection;
    try {
      this.botBlocking.detection = config;
      await apiAuthCaller().patch('/bot-detection', config);
    } catch (error) {
      this.botBlocking.detection = existingConfig;
      showErrorMessage(getApiErrorMessage(error, 'Failed to update bot detection config'));
    }
  }

  @action.bound
  public async getReportingPiiStatus() {
    const {
      userStore,
      messagesStore: { showErrorMessage },
    } = this.rootStore;

    if (!userStore.isOrgAdmin) {
      this.reportingPiiStatus = 'noAccess';
      showErrorMessage(
        getApiErrorMessage({}, 'Only organization admins can access data retention config'),
      );
      return null;
    }

    try {
      const res = await apiAuthCaller().get<RetentionData>('/settings/dataRetention');
      const { enableReportingPii } = res.data;
      this.reportingPiiStatus = enableReportingPii ? 'enabled' : 'disabled';
      return res.data;
    } catch (error) {
      showErrorMessage(getApiErrorMessage(error, 'Failed to get data retention config'));
      return null;
    }
  }

  @computed get dataPlane() {
    return this.dataPlaneURL || '';
  }

  @computed get hasExhaustedTransformationsLimit() {
    const { transformations } = this.rootStore.transformationsListStore;
    return !!this.transformationsLimit && transformations.length >= this.transformationsLimit;
  }

  getTasks() {
    const { messagesStore } = this.rootStore;
    apiAuthCaller()
      .get<{ completed: string[] }>(`/tasks`)
      .then((res) => {
        this.tasks = res.data.completed;
      })
      .catch((err) => {
        messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to get tasks'));
      });
  }

  async markTaskComplete(task: string) {
    await apiAuthCaller()
      .post('/tasks', { task })
      .then(() => {
        this.tasks = (this.tasks || []).concat(task);
      })
      .catch(() => null);
  }

  @computed get dataplaneRegions() {
    return this.dataplanes.length > 0
      ? this.dataplanes.map((dp) => dp.region)
      : [this.dataResidencyDefaultRegion];
  }

  @computed get reportingRegions() {
    return this.dataplanes.length > 1
      ? this.dataplanes.map((dp) => dp.region)
      : [this.defaultRegion];
  }

  // TODO: when the new AccessManagement is enabled user count should be
  // fetched from /users endpoint
  @computed get members() {
    const {
      organizationStore: { members },
    } = this.rootStore;

    return members.filter(
      (member) =>
        member.roles.organization === OrganizationRole.admin ||
        (member.roles.organization === OrganizationRole.member &&
          member.roles.workspaces?.[this.id] &&
          member.roles.workspaces[this.id].length > 0),
    );
  }

  reset() {
    this.id = '';
    this.name = '';
    this.isLoading = true;
    this.createdAt = '';
    this.cloudSourcesLimit = 0;
    this.warehouseActionsLimit = 0;
    this.dataPlaneURL = '';
    this.transformationsLimit = 0;
    this.enableDeviceModeTransformations = false;
    this.enabledSources = [];
    this.features = new Map();
    this.showOnboarding = {} as Record<Onboarding, boolean>;
    this.tasks = undefined;
    this.dataResidencyDefaultRegion = Region.US;
    this.defaultRegion = Region.US;
    this.dataplanes = [];
    this.sshConfig = { publicKey: '', id: '' };
    this.eventAuditEnabled = false;
    this.reportingPiiStatus = 'idle';
    this.metadata = {};
  }
}
